import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { FilePlus, CheckCircle, Warning, ArrowRight, ArrowLeft, FloppyDisk, Circle } from "@phosphor-icons/react";

const CASE_CODES = ["INV","CON","LIT","INF","GEN","ADM","INT","FIR","CoC","FIN","FoR","BD","PERS"];
const CASE_CODE_TOOLTIPS = {
  INV:"Investigation (8-step lifecycle)", CON:"Consulting (scope→invoice)",
  LIT:"Litigation (court flow)", INF:"Infringement (investigation lifecycle)",
  GEN:"General (open→close)", ADM:"Administrative (open→close)",
  INT:"Intelligence (OSINT→report)", FIR:"FIR (police filing lifecycle)",
  CoC:"Chain of Custody", FIN:"Finance · invoice/payment",
  FoR:"Field-only Recon (investigation lifecycle)", BD:"Business Development (sales)",
  PERS:"Personal & confidential — CEO only",
};
const INDUSTRIES = ["FMCG","Pharma","Automotive","Agri-chemicals","Cosmetics","Electronics","Apparel","Luxury","Other"];
const CURRENCIES = ["INR","USD","GBP","EUR","AED","SGD","AUD","BDT","NPR","EGP","CHF"];
const DEPTS = ["Operations","Investigations","Legal","Intelligence","Field Ops"];
const ACTIONS = ["OSINT scan","Target validation","Field dispatch","Test purchase","Legal notice","C&D letter","Government escalation","Raid coordination","Other"];
const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTRY_DEFAULT_CCY = { IN:"INR", US:"USD", GB:"GBP", AE:"AED", SG:"SGD", BD:"BDT", NP:"NPR", AU:"AUD", EG:"EGP", CH:"CHF" };
const LS_KEY = "span_nfor_draft_v2";

const EMPTY = {
  // Tab 1
  client_id: "", is_new_client: false,
  client_legal_name: "", client_country: "IN", industry: "FMCG",
  gstin: "", billing_address: "",
  primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
  // Tab 2
  case_code: "INV", case_title: "", description: "",
  product_at_risk: "", target_entity: "", locations_affected: "",
  counterparty_address: "",
  estimated_value: 0, currency_code: "INR", priority: "medium",
  evidence_available: "no", evidence_description: "",
  first_action_required: "OSINT scan", misc_details: "", attachments: [],
  // Tab 3
  assigned_to_user_id: "", secondary_assignee_user_id: "",
  date_of_contact: new Date().toISOString().substring(0,10),
  client_contact_persons: "",
  assigned_department: "Operations",
  target_completion_date: "", legacy_reference: "", special_instructions: "",
  escalate_immediately: false,
};

export default function CaseNFOR() {
  const nav = useNavigate();
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const saveTimer = useRef(null);

  // Initial load — fetch clients, users, current user, and any saved draft
  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || []));
    api.get("/users").then(r => setUsers(r.data || []));
    api.get("/auth/me").then(r => setMe(r.data)).catch(() => {});
    // Server draft fallback first
    api.get("/cases/nfor/draft").then(r => {
      if (r.data?.payload) { setForm({ ...EMPTY, ...r.data.payload }); setDraftRestored(true); return; }
      try {
        const ls = JSON.parse(localStorage.getItem(LS_KEY) || "null");
        if (ls) { setForm({ ...EMPTY, ...ls }); setDraftRestored(true); }
      } catch {}
    });
  }, []);

  // Auto-save draft every 30s (debounced on any field change)
  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch {}
      api.put("/cases/nfor/draft", { payload: form }).catch(() => {});
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [form]);

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

  // Existing client → autofill
  const selectClient = (cid) => {
    const c = clients.find(x => x.client_id === cid);
    if (c) {
      update({
        client_id: c.client_id, is_new_client: false,
        client_legal_name: c.name, client_country: c.country_code || "IN",
        industry: c.industry || "Other",
        gstin: c.gstin || "",
        billing_address: c.billing_address || "",
        primary_contact_name: c.primary_contact_name || c.primary_contact || "",
        primary_contact_email: c.email || "",
        primary_contact_phone: c.phone || "",
        currency_code: COUNTRY_DEFAULT_CCY[c.country_code || "IN"] || "INR",
      });
    } else update({ client_id: "" });
  };

  // Validation per tab
  const tabErrors = () => {
    const e = [];
    if (tab === 0) {
      if (!form.client_legal_name) e.push("client_legal_name");
      if (!form.client_country) e.push("client_country");
      if (!form.industry) e.push("industry");
      if (!form.billing_address) e.push("billing_address");
      if (!form.primary_contact_name) e.push("primary_contact_name");
      if (!form.primary_contact_email || !EMAIL_RX.test(form.primary_contact_email)) e.push("primary_contact_email");
      if (form.client_country === "IN" && form.gstin && !GSTIN_RX.test(form.gstin.toUpperCase())) e.push("gstin");
    }
    if (tab === 1) {
      if (!form.case_code) e.push("case_code");
      if (!form.case_title || form.case_title.length > 120) e.push("case_title");
      if (!form.description || form.description.length < 100) e.push("description");
      if (!form.product_at_risk) e.push("product_at_risk");
      if (!form.target_entity) e.push("target_entity");
      if (!form.locations_affected) e.push("locations_affected");
      if (!form.evidence_available) e.push("evidence_available");
      if (!form.first_action_required) e.push("first_action_required");
    }
    if (tab === 2) {
      if (!form.assigned_to_user_id) e.push("assigned_to_user_id");
      if (!form.date_of_contact) e.push("date_of_contact");
      if (!form.client_contact_persons) e.push("client_contact_persons");
      if (!form.assigned_department) e.push("assigned_department");
    }
    return e;
  };
  const errs = tabErrors();
  const isPERS = form.case_code === "PERS";
  const showPERS = me && (me.role === "CEO" || me.department === "Executive");

  const submit = async () => {
    const all = [...tabErrors()];
    setTab(2);
    if (all.length) { setErr("Please complete required fields"); return; }
    setSubmitting(true); setErr(null);
    try {
      const payload = { ...form };
      if (form.client_id) payload.is_new_client = false;
      const r = await api.post("/cases/nfor-v2", payload);
      try { localStorage.removeItem(LS_KEY); } catch {}
      setConfirm(r.data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (confirm) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="nfor-confirmation">
        <div className="card-flat p-7 text-center">
          <div className="text-emerald-600 mb-3"><CheckCircle size={48} weight="fill" className="mx-auto"/></div>
          <h1 className="font-display font-bold text-2xl">File opened successfully</h1>
          <div className="mt-3 grid grid-cols-2 gap-3 text-left text-[12px] hairline p-4 bg-[var(--span-surface-2)]">
            <Cell label="Case code" value={confirm.case_code} testid="conf-case-code"/>
            <Cell label="Our file reference" value={confirm.our_file_reference} testid="conf-file-ref"/>
            <Cell label="Assigned to" value={confirm.assigned_to_name || "—"}/>
            <Cell label="First milestone" value={confirm.first_milestone || "—"}/>
          </div>
          {confirm.escalated && (
            <div className="mt-3 text-[12px] bg-red-50 text-red-700 hairline p-2.5" data-testid="conf-escalated">
              <Warning size={12} weight="duotone" className="inline mr-1"/> Auto-escalated to CEO · {confirm.escalation_reason}
            </div>
          )}
          {confirm.client_code_pending && (
            <div className="mt-2 text-[11px] bg-amber-50 text-amber-700 hairline p-2">
              Client code pending — Operations will assign it.
            </div>
          )}
          <div className="text-[11px] text-[var(--span-text-2)] mt-2">
            Stakeholder alerts queued to {confirm.stakeholder_alerts?.length || 0} recipient(s).
          </div>
          <div className="flex gap-2 justify-center mt-5">
            <button data-testid="conf-open-case" onClick={() => nav(`/cases/${confirm.case_id}`)} className="span-btn">Open case</button>
            <button data-testid="conf-another" onClick={() => { setConfirm(null); setForm(EMPTY); setTab(0); }} className="text-[11px] hairline px-3 py-1.5">Open another NFOR</button>
            <button data-testid="conf-dashboard" onClick={() => nav("/dashboard")} className="text-[11px] hairline px-3 py-1.5">Go to dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="case-nfor-page">
      <div className="label-eyebrow flex items-center gap-2"><FilePlus size={12}/> New File Opening Request</div>
      <h1 className="font-display font-bold text-3xl mt-1">Open a new SPAN case</h1>
      <div className="text-[12px] text-[var(--span-text-2)] mt-1 flex items-center gap-2">
        <span>Replaces JotForm · auto-saved every ~2s · 4-way stakeholder alerts on submit.</span>
        {draftRestored && <span className="badge badge-info text-[10px]" data-testid="draft-restored">draft restored</span>}
      </div>

      {/* Tab navigator */}
      <div className="mt-5 hairline p-0 flex" data-testid="nfor-tabs">
        {["Client details","Case details","Assignment"].map((label, i) => {
          const done = i < tab;  // crude: a tab is "done" if we moved past it
          const active = tab === i;
          return (
            <button key={i} onClick={() => setTab(i)} data-testid={`tab-${i}`}
                    className={`flex-1 px-4 py-3 text-[12px] text-left border-r last:border-r-0 ${active ? "bg-[var(--span-surface-3)] font-bold" : "hover:bg-[var(--span-surface-2)]"}`}>
              <div className="flex items-center gap-1.5">
                {done ? <CheckCircle size={12} weight="fill" className="text-emerald-600"/> : <Circle size={12}/>}
                <span>{i+1}. {label}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="card-flat p-5 mt-2 space-y-3">
        {tab === 0 && (
          <ClientDetailsTab form={form} update={update} clients={clients} errs={errs} selectClient={selectClient}/>
        )}
        {tab === 1 && (
          <CaseDetailsTab form={form} update={update} errs={errs} showPERS={showPERS}/>
        )}
        {tab === 2 && (
          <AssignmentTab form={form} update={update} users={users} errs={errs}/>
        )}

        {err && <div className="text-red-600 text-[12px]" data-testid="nfor-err">{err}</div>}

        <div className="flex justify-between pt-3 border-t">
          <button data-testid="nfor-back" disabled={tab === 0} onClick={() => setTab(t => t-1)} className="text-[11px] hairline px-3 py-1.5 disabled:opacity-50">
            <ArrowLeft size={11} className="inline mr-1"/>Back
          </button>
          {tab < 2 ? (
            <button data-testid="nfor-next" onClick={() => { if (errs.length === 0) setTab(t => t+1); else setErr("Please complete required fields on this tab"); }} className="span-btn">
              Next <ArrowRight size={11}/>
            </button>
          ) : (
            <button data-testid="nfor-submit" disabled={submitting} onClick={submit} className="span-btn">
              {submitting ? "Filing…" : "Submit NFOR"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display font-bold mt-0.5">{value || "—"}</div>
    </div>
  );
}

function Field({ label, required, error, children, hint }) {
  return (
    <div>
      <label className="label-eyebrow block mb-0.5">{label} {required && <span className="text-red-500">*</span>}</label>
      {children}
      {hint && !error && <div className="text-[10px] text-[var(--span-text-2)] mt-0.5">{hint}</div>}
      {error && <div className="text-[10px] text-red-600 mt-0.5">Required or invalid</div>}
    </div>
  );
}

function Input({ value, onChange, type="text", required, "data-testid": testid, ...rest }) {
  return <input type={type} required={required} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full hairline px-2 py-1.5 text-[12px]" {...rest}/>;
}

function Textarea({ value, onChange, rows=3, "data-testid": testid, ...rest }) {
  return <textarea rows={rows} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} className="w-full hairline px-2 py-1.5 text-[12px]" {...rest}/>;
}

function ClientDetailsTab({ form, update, clients, errs, selectClient }) {
  return (
    <>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-[12px]" data-testid="new-client-toggle">
          <input type="checkbox" checked={form.is_new_client} onChange={e => update({ is_new_client: e.target.checked, client_id: e.target.checked ? "" : form.client_id })}/>
          This is a new client
        </label>
      </div>
      {form.is_new_client && (
        <div className="bg-amber-50 hairline p-2 text-[11px] text-amber-800" data-testid="new-client-notice">
          New client — code will be assigned by Operations.
        </div>
      )}
      {!form.is_new_client && (
        <Field label="Client (autocomplete)" required>
          <select value={form.client_id} onChange={e => selectClient(e.target.value)} data-testid="client-autocomplete" className="w-full hairline px-2 py-1.5 text-[12px]">
            <option value="">— Select an existing client —</option>
            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name} ({c.country_code || "?"})</option>)}
          </select>
        </Field>
      )}
      <Field label="Client legal name" required error={errs.includes("client_legal_name")}>
        <Input value={form.client_legal_name} onChange={v => update({ client_legal_name: v.slice(0,120) })} data-testid="client-legal-name"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client country (ISO-2)" required error={errs.includes("client_country")}>
          <Input value={form.client_country} onChange={v => update({ client_country: v.toUpperCase().slice(0,2), currency_code: COUNTRY_DEFAULT_CCY[v.toUpperCase().slice(0,2)] || form.currency_code })} data-testid="client-country" maxLength={2}/>
        </Field>
        <Field label="Industry" required error={errs.includes("industry")}>
          <select value={form.industry} onChange={e => update({ industry: e.target.value })} data-testid="client-industry" className="w-full hairline px-2 py-1.5 text-[12px]">
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
      </div>
      {form.client_country === "IN" && (
        <Field label="GSTIN (India)" hint="Format: 22AAAAA0000A1Z5" error={errs.includes("gstin")}>
          <Input value={form.gstin} onChange={v => update({ gstin: v.toUpperCase() })} data-testid="client-gstin"/>
        </Field>
      )}
      <Field label="Billing address" required error={errs.includes("billing_address")}>
        <Textarea value={form.billing_address} onChange={v => update({ billing_address: v })} data-testid="billing-address"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Primary contact name" required error={errs.includes("primary_contact_name")}>
          <Input value={form.primary_contact_name} onChange={v => update({ primary_contact_name: v })} data-testid="primary-contact-name"/>
        </Field>
        <Field label="Primary contact email" required error={errs.includes("primary_contact_email")}>
          <Input type="email" value={form.primary_contact_email} onChange={v => update({ primary_contact_email: v })} data-testid="primary-contact-email"/>
        </Field>
      </div>
      <Field label="Primary contact phone" hint="Include country code">
        <Input value={form.primary_contact_phone} onChange={v => update({ primary_contact_phone: v })} data-testid="primary-contact-phone"/>
      </Field>
    </>
  );
}

function CaseDetailsTab({ form, update, errs, showPERS }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Case code (service code)" required error={errs.includes("case_code")} hint={CASE_CODE_TOOLTIPS[form.case_code]}>
          <select value={form.case_code} onChange={e => update({ case_code: e.target.value })} data-testid="case-code" className="w-full hairline px-2 py-1.5 text-[12px]">
            {CASE_CODES.filter(c => c !== "PERS" || showPERS).map(c => <option key={c} value={c}>{c} — {CASE_CODE_TOOLTIPS[c].slice(0,40)}</option>)}
          </select>
        </Field>
        <Field label="Priority" required>
          <select value={form.priority} onChange={e => update({ priority: e.target.value })} data-testid="case-priority" className="w-full hairline px-2 py-1.5 text-[12px]">
            {["low","medium","high","critical"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Case title" required error={errs.includes("case_title")} hint="Max 120 chars">
        <Input value={form.case_title} onChange={v => update({ case_title: v.slice(0,120) })} data-testid="case-title"/>
      </Field>
      <Field label="Nature of work / description" required error={errs.includes("description")} hint={`${form.description.length} / 100 minimum`}>
        <Textarea value={form.description} rows={5} onChange={v => update({ description: v })} data-testid="case-description"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Product / brand at risk" required error={errs.includes("product_at_risk")}>
          <Input value={form.product_at_risk} onChange={v => update({ product_at_risk: v })} data-testid="product-at-risk"/>
        </Field>
        <Field label="Target entity / counterparty" required error={errs.includes("target_entity")}>
          <Input value={form.target_entity} onChange={v => update({ target_entity: v })} data-testid="target-entity"/>
        </Field>
      </div>
      <Field label="Location(s) affected" required error={errs.includes("locations_affected")} hint="Comma-separated cities, e.g. 'Malda, Burdwan, Kolkata'">
        <Textarea value={form.locations_affected} onChange={v => update({ locations_affected: v })} data-testid="locations-affected"/>
      </Field>
      <Field label="Counterparty address (optional)">
        <Textarea value={form.counterparty_address} onChange={v => update({ counterparty_address: v })} data-testid="counterparty-address"/>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Estimated value" required>
          <Input type="number" value={form.estimated_value} onChange={v => update({ estimated_value: parseFloat(v) || 0 })} data-testid="estimated-value"/>
        </Field>
        <Field label="Currency" required>
          <select value={form.currency_code} onChange={e => update({ currency_code: e.target.value })} data-testid="currency-code" className="w-full hairline px-2 py-1.5 text-[12px]">
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="First action required" required error={errs.includes("first_action_required")}>
          <select value={form.first_action_required} onChange={e => update({ first_action_required: e.target.value })} data-testid="first-action" className="w-full hairline px-2 py-1.5 text-[12px]">
            {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Evidence available?" required error={errs.includes("evidence_available")}>
        <div className="flex gap-2">
          {["yes","no","partial"].map(v => (
            <label key={v} className={`flex-1 hairline px-3 py-2 text-[12px] text-center cursor-pointer ${form.evidence_available === v ? "bg-emerald-50 border-emerald-400" : ""}`}>
              <input type="radio" name="ev" value={v} checked={form.evidence_available === v} onChange={e => update({ evidence_available: e.target.value })} data-testid={`evidence-${v}`} className="sr-only"/>
              {v}
            </label>
          ))}
        </div>
      </Field>
      {form.evidence_available === "partial" && (
        <Field label="Describe available evidence">
          <Textarea value={form.evidence_description} onChange={v => update({ evidence_description: v })} data-testid="evidence-description"/>
        </Field>
      )}
      <Field label="Misc details (optional)">
        <Textarea value={form.misc_details} onChange={v => update({ misc_details: v })} data-testid="misc-details"/>
      </Field>
    </>
  );
}

function AssignmentTab({ form, update, users, errs }) {
  const filtered = users.filter(u => ["Operations","Investigations","FieldOps","Field Ops"].includes(u.department) || u.role === "CEO" || u.role === "Admin");
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Assigned to (AM/PM)" required error={errs.includes("assigned_to_user_id")}>
          <select value={form.assigned_to_user_id} onChange={e => update({ assigned_to_user_id: e.target.value })} data-testid="assigned-to" className="w-full hairline px-2 py-1.5 text-[12px]">
            <option value="">— Select assignee —</option>
            {filtered.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.department}</option>)}
          </select>
        </Field>
        <Field label="Secondary assignee (optional)">
          <select value={form.secondary_assignee_user_id} onChange={e => update({ secondary_assignee_user_id: e.target.value })} data-testid="secondary-assignee" className="w-full hairline px-2 py-1.5 text-[12px]">
            <option value="">—</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.department}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of contact" required error={errs.includes("date_of_contact")}>
          <Input type="date" value={form.date_of_contact} onChange={v => update({ date_of_contact: v })} data-testid="date-of-contact"/>
        </Field>
        <Field label="Assigned department" required error={errs.includes("assigned_department")}>
          <select value={form.assigned_department} onChange={e => update({ assigned_department: e.target.value })} data-testid="assigned-dept" className="w-full hairline px-2 py-1.5 text-[12px]">
            {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Name of contact person(s)" required error={errs.includes("client_contact_persons")} hint="Person(s) at the client who gave the instruction">
        <Input value={form.client_contact_persons} onChange={v => update({ client_contact_persons: v })} data-testid="contact-persons"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Target completion date (optional)">
          <Input type="date" value={form.target_completion_date} onChange={v => update({ target_completion_date: v })} data-testid="target-completion"/>
        </Field>
        <Field label="Internal reference (legacy, optional)" hint="e.g. IR307-23">
          <Input value={form.legacy_reference} onChange={v => update({ legacy_reference: v })} data-testid="legacy-reference"/>
        </Field>
      </div>
      <Field label="Special instructions (max 500)" hint={`${form.special_instructions.length} / 500`}>
        <Textarea value={form.special_instructions} rows={3} onChange={v => update({ special_instructions: v.slice(0,500) })} data-testid="special-instructions"/>
      </Field>
      <label className="flex items-center gap-2 text-[12px]" data-testid="escalate-toggle">
        <input type="checkbox" checked={form.escalate_immediately} onChange={e => update({ escalate_immediately: e.target.checked })}/>
        Escalate immediately to CEO (overrides value threshold)
      </label>
    </>
  );
}
